import { useState, type ReactElement } from 'react';

import { illustrations } from '../../assets/illustrations';
import { SensitivityChip } from '../../atoms/SensitivityChip';
import { TagChip } from '../../atoms/TagChip';
import { MaskIcon } from '../../lib/mask-icon';
import { DRILL_PRIMARY_BUTTON_CLASS, UserTypeDrillButton } from '../../lib/user-type-drill';
import { cn } from '../../lib/utils';
import { EventCountStrip } from '../../molecules/EventCountStrip';
import { FilterPillGroup } from '../../molecules/FilterPillGroup';
import { IconCount } from '../../molecules/IconCount';

/**
 * Pre-shaped row for a single file in the Top Data Accessed widget. Mirrors
 * the dashboard API's `TopDataAccessedRow` minus the `drilldown` field: the
 * wrapper keeps that and turns `onRowClick` / `onUserTypeClick` into the link.
 */
export interface TopDataAccessedTabRow {
  /** Stable row id (drives React key + `data-testid="top-data-row-{id}"`). */
  id: string;
  fileName: string;
  documentTopic: string;
  /** Stable sensitivity id from the API (drives chip tint). */
  sensitivityId: string;
  /** Display label for the sensitivity chip ("Highly Confidential" etc.). */
  sensitivityLabel: string;
  accessEvents: number;
  events: { read: number; write: number; download: number; delete: number };
  distinctHICount: number;
  distinctNHICount: number;
  /** Forward-compat — preserved on the prop type but renders no visual today. */
  isHighlighted?: boolean;
  /**
   * False keeps this row plain even when the body has click handlers, for a
   * file the consumer cannot link to. Defaults to true.
   */
  isInteractive?: boolean;
}

export interface TopDataAccessedTab {
  id: string;
  /** Display label rendered as `${label} (${count})` on the pill, the count comma-grouped (`153,442`). */
  label: string;
  count: number;
  /** Pre-segmented rows for this tab. May be empty. */
  rows: ReadonlyArray<TopDataAccessedTabRow>;
}

export interface TopDataAccessedBodyProps {
  /** Pre-ordered tabs from the API: the `all` tab, one per sensitivity tier, and a trailing unclassified tab. */
  tabs: ReadonlyArray<TopDataAccessedTab>;
  /** Default selected pill id. Defaults to `'all'`. */
  defaultTabId?: string;
  /**
   * Row click (TDEV-1116). When supplied, each interactive row takes a mouse
   * click and its file name is a `<button>` for the keyboard; when omitted, rows
   * are plain `<div>`s.
   */
  onRowClick?: (row: TopDataAccessedTabRow) => void;
  /** HI / NHI count click: that file for that identity kind. Each count is its own button. */
  onUserTypeClick?: (row: TopDataAccessedTabRow, kind: 'HI' | 'NHI') => void;
  className?: string;
}

/**
 * Body grid template — 7 column tracks at Figma `5:23313` widths
 * (180/144/108/108/240/108/108 = 996px). The Events column is a single
 * 240px slot; each row renders its own internal `EventCountStrip` with a
 * fixed 4×48px sub-grid + 12px gaps per Figma `5:23356`, NOT subgrid —
 * subgrid would inherit the body's `justify-between` outer-column gap
 * distribution, which inflates the inner sub-cell gaps far beyond Figma's
 * 12px. Cross-row icon alignment within the events column still holds:
 * the events slot starts at the same global x in every row, and the
 * fixed 48px sub-cells align icons at deterministic offsets (8/68/128/188).
 *
 * The File Name track is `minmax(0, 180px)`: 996px of tracks is 6px wider
 * than the 990px body at a 1280px viewport with the sidebar open, and the
 * name is the one cell that already truncates. At 996px or wider it is 180px.
 */
const BODY_GRID =
  'grid w-full items-center justify-between grid-cols-[minmax(0,180px)_144px_108px_108px_240px_108px_108px]';

const DATA_GRID_COLS = 'grid-cols-[minmax(0,180px)_144px_108px_108px_240px_108px_108px]';

/**
 * `TopDataAccessedBody` — body of the Top Data Accessed dashboard widget per
 * Figma `5:23313`. Composes the canonical pill row (`FilterPillGroup`) +
 * 7-column row table. Owns local `selectedTabId` state; defaults to `'all'`.
 * Empty tabs render an inline "No files found in the selected time window."
 * helper and the pills stay interactive.
 *
 * Layout: a single 10-column CSS grid spans header + every data row. Header
 * and rows use `display: contents` so their cell `<div>`s flow directly into
 * the parent grid. The events column expands into 4 auto-sized sub-tracks so
 * cells across rows share column widths (icons line up regardless of digit
 * count, just like an HTML table).
 *
 * Rows are `<div>`s. With `onRowClick` a row takes a mouse click and its file
 * name becomes a `<button>`; with `onUserTypeClick` the HI / NHI counts become
 * buttons that stop the click from reaching the row. The
 * `data-testid="top-data-row-{id}"` is the contract surface for
 * wrapper/integration tests.
 *
 * `@dawn/ui` agnosticism preserved — this organism imports no values from
 * `@dawn/feature-config`. The wrapper passes the tab list verbatim.
 */
export function TopDataAccessedBody({
  tabs,
  defaultTabId = 'all',
  onRowClick,
  onUserTypeClick,
  className,
}: TopDataAccessedBodyProps): ReactElement {
  const [selectedTabId, setSelectedTabId] = useState(defaultTabId);

  const pillOptions = tabs.map((t) => ({
    value: t.id,
    label: `${t.label} (${t.count.toLocaleString('en-US')})`,
  }));
  const visibleRows = tabs.find((t) => t.id === selectedTabId)?.rows ?? [];

  return (
    <div
      className={cn('flex w-full flex-col gap-4 font-outfit text-dawn-black', className)}
      data-testid="top-data-accessed-body"
    >
      <FilterPillGroup options={pillOptions} value={selectedTabId} onChange={setSelectedTabId} />

      <div className={BODY_GRID}>
        <HeaderRow />
        <div
          className={cn(
            'col-span-7 mt-4 grid w-full items-center justify-between gap-y-1',
            DATA_GRID_COLS
          )}
        >
          {visibleRows.length === 0 ? (
            <p
              data-testid="top-data-empty-helper"
              className="col-span-7 px-2 py-4 text-p2 text-grey-500"
            >
              No files found in the selected time window.
            </p>
          ) : (
            visibleRows.map((row) => (
              <Row
                key={row.id}
                row={row}
                onRowClick={onRowClick}
                onUserTypeClick={onUserTypeClick}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

TopDataAccessedBody.displayName = 'TopDataAccessedBody';

const HEADER_LABELS = {
  fileName: 'File Name',
  documentTopic: 'Document Topic',
  sensitivity: 'Sensitivity',
  accessEvents: 'Access Events',
  events: 'Events',
  hi: 'HI Access',
  nhi: 'NHI Access',
} as const satisfies Readonly<Record<string, string>>;

// Bracketed font-size literal + colour utility on the same element is safe
// here: the bracketed literal sits in tailwind-merge's font-size group, which
// no longer collides with the `text-grey-600` colour utility (the collision
// is specific to the custom `text-p*` tokens — see TagChip pattern).
const HEADER_CELL = 'px-2 py-2 text-[12px] font-normal leading-[1.4] text-grey-600';

function HeaderRow(): ReactElement {
  return (
    <div data-testid="top-data-header-row" className="contents">
      <div className={HEADER_CELL}>{HEADER_LABELS.fileName}</div>
      <div className={HEADER_CELL}>{HEADER_LABELS.documentTopic}</div>
      <div className={HEADER_CELL}>{HEADER_LABELS.sensitivity}</div>
      <div className={HEADER_CELL}>{HEADER_LABELS.accessEvents}</div>
      <div className={HEADER_CELL}>{HEADER_LABELS.events}</div>
      <div className={HEADER_CELL}>{HEADER_LABELS.hi}</div>
      <div className={HEADER_CELL}>{HEADER_LABELS.nhi}</div>
      {/*
       * Full-width header→body divider per Figma `5:23328`: continuous
       * 1px `grey-200` line spanning all 7 grid tracks. Per-cell borders
       * would break at column gaps under `justify-between`, so the line
       * lives on a dedicated row that grid auto-flow places below the
       * header cells.
       */}
      <div data-testid="top-data-header-divider" className="col-span-7 border-b border-grey-200" />
    </div>
  );
}

// Row cells carry no bottom border per Figma `5:23313` — only the header→body
// divider (HEADER_CELL) renders a grey-100 line. Per-row separators were a
// BUILD-era carryover and not present in the design.
// Fixed h-[28px] matches Figma row height; flex items-center vertically centers content.
const ROW_CELL = 'h-[28px] flex items-center';

function Row({
  row,
  onRowClick,
  onUserTypeClick,
}: {
  row: TopDataAccessedTabRow;
  onRowClick?: (row: TopDataAccessedTabRow) => void;
  onUserTypeClick?: (row: TopDataAccessedTabRow, kind: 'HI' | 'NHI') => void;
}): ReactElement {
  const interactive = row.isInteractive !== false;
  const handleRowClick = interactive && onRowClick ? () => onRowClick(row) : undefined;
  const identityCount = (kind: 'HI' | 'NHI', count: ReactElement): ReactElement =>
    interactive && onUserTypeClick ? (
      <UserTypeDrillButton
        kind={kind}
        subject={row.fileName}
        onClick={() => onUserTypeClick(row, kind)}
      >
        {count}
      </UserTypeDrillButton>
    ) : (
      count
    );

  return (
    <div
      data-testid={`top-data-row-${row.id}`}
      className={cn('contents text-p1', handleRowClick && 'group/drill cursor-pointer')}
      onClick={handleRowClick}
    >
      <div
        className={cn(ROW_CELL, 'truncate px-2 text-[10px] leading-[1.2] text-dawn-black')}
        title={row.fileName}
      >
        {handleRowClick ? (
          // Activation bubbles to the row's onClick.
          <button type="button" className={cn(DRILL_PRIMARY_BUTTON_CLASS, 'truncate')}>
            {row.fileName}
          </button>
        ) : (
          row.fileName
        )}
      </div>
      <div className={cn(ROW_CELL, 'px-2')}>
        <TagChip state="subtle" size="sm">
          {row.documentTopic}
        </TagChip>
      </div>
      <div className={cn(ROW_CELL, 'px-2')}>
        <SensitivityChip level={row.sensitivityId} label={row.sensitivityLabel} size="sm" />
      </div>
      <div className={cn(ROW_CELL, 'px-2')}>
        <IconCount
          icon={<MaskIcon src={illustrations.fileOutline} size={16} />}
          count={row.accessEvents}
          size={16}
        />
      </div>
      {/*
       * Events strip in standalone (default) mode — 4×48px sub-cells with
       * 12px gaps per Figma `5:23356`. Cross-row icon alignment is preserved
       * because the events slot starts at the same global x in every row
       * (the body grid uses fixed 240px for this column).
       */}
      <div className={cn(ROW_CELL, 'px-2')}>
        {/*
         * `view` and `share` stay at 0: `EventCountStrip` resolves
         * cell 1 as `read ?? view` and cell 3 as `write ?? share`,
         * so the override values always win. Explicit zero is
         * self-documenting per Greptile review on PR #85.
         */}
        <EventCountStrip
          view={0}
          read={row.events.read}
          download={row.events.download}
          share={0}
          write={row.events.write}
          delete={row.events.delete}
          dimZeroes
        />
      </div>
      <div className={cn(ROW_CELL, 'px-2')}>
        {identityCount(
          'HI',
          <IconCount
            icon={<MaskIcon src={illustrations.humanIdentityOutline} size={16} />}
            count={row.distinctHICount}
            size={16}
          />
        )}
      </div>
      <div className={cn(ROW_CELL, 'px-2')}>
        {identityCount(
          'NHI',
          <IconCount
            icon={<MaskIcon src={illustrations.nonHumanIdentityOutline} size={16} />}
            count={row.distinctNHICount}
            size={16}
          />
        )}
      </div>
    </div>
  );
}
